import * as React from 'react';
import styled from 'styled-components';
import {ComponentS, ComponentTextStyle} from '../../styles/typography';
import Checkbox from '../../InputFields/Checkbox';
import {Tag, TagVariants} from '../../Tag';

import {COLORS} from '../../index';
import {ImageWithFallbacks} from "../../Image";

export interface CardTopSectionProps {
  selected?: boolean;
  setSelected?: (arg0: boolean) => void;
  tagLabel?: string;
  tagIcon?: React.ReactNode;
  tagVariant?: TagVariants;
  highlightRibbonText?: string;
  highlightRibbonIcon?: React.ReactNode;
  highlightRibbonContentColor?: string;
  highlightRibbonBgColor?: string;
  image?: { src: string; fallbackSrc?: string; alt: string, height?: string; width?: string; loader?: boolean };
  disabled?: boolean;
}

export const CardTopSectionImageContainer = styled.div`
  border-top-right-radius: 8px;
  border-top-left-radius: 8px;
  height: 100%;
  width: 100%;
  img{
    object-fit: cover;
    width: 100%;
    height: 100%;
    border-top-right-radius: 8px;
    border-top-left-radius: 8px;
  }
`;

export const CardTopSectionTagContainer = styled.div`
  position: absolute;

  top: 16px;
  left: 16px;
  width: calc(100% - 32px);
`;

export const CardTopSectionContainer = styled.div<{ disabled?: boolean }>`
  position: relative;
  width: 100%;
  overflow: visible;

  ${props => props.disabled ? `
  img, svg {
    filter: grayscale(100%);
  }` : ''}
`;

export const CardTopSectionRibbonContainer = styled.div<{ $color: string; $backgroundColor: string }>`
  min-height: calc(40px - 16px);
  background-color: ${props => props.$backgroundColor};
  width: calc(100% - 32px);
  position: absolute;
  bottom: 0px;
  left: 0px;
  padding: 8px 16px 8px 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  svg {
    color: ${props => props.$color};
    width: 24px;
    height: 24px;
    flex-grow: 0;
    flex-shrink: 0;
  }
`;

export const CardTopSectionCheckboxContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;

  .checkbox-icon {
    background-color: ${COLORS.white};
  }
`;

const CardTopSection = React.forwardRef(({
                                           selected,
                                           setSelected,
                                           image,
                                           tagLabel,
                                           tagIcon,
                                           tagVariant = 'positive',
                                           highlightRibbonIcon,
                                           highlightRibbonText,
                                           highlightRibbonContentColor,
                                           highlightRibbonBgColor,
                                           disabled
                                         }: CardTopSectionProps, ref: React.Ref<HTMLDivElement>) => {

  return (
    <CardTopSectionContainer disabled={disabled} data-testid={'card-topSection'} style={{height: image?.height ?? 200}}>
      {
        image &&
        <CardTopSectionImageContainer>
            <ImageWithFallbacks fallbacks={image.fallbackSrc ?? ''} src={image.src} alt={image.alt} loader={image.loader ?? false} width={image.width} height={image.height}/>
        </CardTopSectionImageContainer>
      }
      {(!!selected || !!setSelected) && (
        <CardTopSectionCheckboxContainer data-testid={'card-topSection-checkbox'}>
          <Checkbox ref={ref}
                    disabled={disabled}
                    select={(selected: boolean) => setSelected && setSelected(selected)}
                    selected={selected || false}/>
        </CardTopSectionCheckboxContainer>
      )}
      {(tagLabel || tagIcon) && (
        <CardTopSectionTagContainer>
          <Tag label={tagLabel} variant={tagVariant} icon={tagIcon}/>
        </CardTopSectionTagContainer>
      )}
      {(highlightRibbonIcon || highlightRibbonText) && (
        <CardTopSectionRibbonContainer data-testid={'card-topSection-ribbon'} $color={highlightRibbonContentColor ?? ''}
                             $backgroundColor={disabled ? COLORS.neutral_300 : highlightRibbonBgColor ?? ''}>
          {highlightRibbonIcon}
          {highlightRibbonText && (
            <ComponentS color={highlightRibbonContentColor ?? ''} textStyle={ComponentTextStyle.Regular}>
              {highlightRibbonText}
            </ComponentS>
          )}
        </CardTopSectionRibbonContainer>
      )}
    </CardTopSectionContainer>
  );
});

export default CardTopSection;
